import dayjs from 'dayjs';
import React, { Fragment } from 'react';
import { useSelector } from 'react-redux';

import { Grid } from '@mui/material';
import CachedIcon from '@mui/icons-material/Cached';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import AutoFixNormalIcon from '@mui/icons-material/AutoFixNormal';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';

import { Autocomplete, Button, TextField } from 'shared';
import { handleErrorAlert } from 'utils/handleErrorAlert';
import { matches } from 'components/Inventory/utils/matches';
import { handleSuccessAlert } from 'utils/handleSuccessAlert';
import { useInventoryService } from 'store/services/inventory.service';
import { forReviewFiltersStyles } from 'components/Inventory/style/style';
import { types } from 'components/Inventory/utils/forReviewTransactionTypes';

const ForReviewFilters = ({
    data,
    banks,
    values,
    selected,
    handleReset,
    setSelected,
    handleChange,
    setAlertData,
    setFieldValue,
    handleAIMatch,
    autoMatchLoading,
    handleClearClick,
    handleStartAutoMatch,
    getInitialInventoryForReview,
}) => {
    const classes = forReviewFiltersStyles();
    const companyData = useSelector((state) => state.company.data);

    const { isLoading, excludePlaidTransaction } = useInventoryService();

    const hangleExcludeAllButtonClick = async () => {
        try {
            const response = await excludePlaidTransaction({
                company_id: companyData?.id,
                plaid_transaction_ids: selected,
            });
            handleSuccessAlert(setAlertData, response?.message);
            setSelected([]);
            getInitialInventoryForReview(1);
        } catch (error) {
            handleErrorAlert(setAlertData);
        }
    };

    return (
        <Fragment>
            <Grid container spacing={2}>
                <Grid item xs={6} md={4} lg={3}>
                    <Autocomplete
                        options={banks}
                        labelKey="label"
                        valueKey="value"
                        placeholder="Bank Account"
                        name="bankAccount"
                        inlineLabel="Bank Account"
                        value={banks.find((bank) => bank.value == values.bankAccount)}
                        onChange={(e, value) => setFieldValue('bankAccount', value.value)}
                        isSearchField={false}
                        disableClearable
                        customStyle={classes.dropdown}
                        showTotal={true}
                    />
                </Grid>
                <Grid item xs={4} md={2} lg={2}>
                    <TextField
                        fullWidth
                        name="amountBuffer"
                        onChange={(e) => {
                            const val = e.target.value;
                            if (/^\d*\.?\d*$/.test(val)) {
                                handleChange(e);
                            }
                        }}
                        inlineLabel="Amount Buffer"
                        placeholder="Amount Buffer"
                        value={values.amountBuffer}
                        customStyle={classes.inputField}
                    />
                </Grid>
                <Grid item xs={4} md={2} lg={2}>
                    <TextField
                        fullWidth
                        name="dateBuffer"
                        inlineLabel="Date Buffer"
                        value={values.dateBuffer}
                        placeholder="Date Buffer"
                        customStyle={classes.inputField}
                        onChange={(e) => {
                            const val = e.target.value;
                            if (/^\d*$/.test(val)) {
                                handleChange(e);
                            }
                        }}
                    />
                </Grid>
                <Grid item xs={10} md={4} lg={4} />

                <Grid item xs={4} md={2} lg={2}>
                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                        <DatePicker
                            value={values.startDate && dayjs(values.startDate)}
                            onChange={(date) => setFieldValue('startDate', date)}
                            label="Start Date"
                            placeholder="Start Date"
                            format="MM-DD-YYYY"
                            sx={classes.datePicker}
                        />
                    </LocalizationProvider>
                </Grid>
                <Grid item xs={4} md={2} lg={2}>
                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                        <DatePicker
                            value={values.endDate && dayjs(values.endDate)}
                            onChange={(date) => setFieldValue('endDate', date)}
                            label="End Date"
                            placeholder="End Date"
                            format="MM-DD-YYYY"
                            sx={classes.datePicker}
                        />
                    </LocalizationProvider>
                </Grid>
                <Grid item xs={4} md={2} lg={2}>
                    <TextField
                        fullWidth
                        name="amount"
                        inlineLabel="Amount"
                        value={values.amount}
                        placeholder="Search by amount"
                        customStyle={classes.inputField}
                        onChange={(e) => {
                            const val = e.target.value;
                            if (/^-?\d*\.?\d*$/.test(val)) {
                                handleChange(e);
                            }
                        }}
                    />
                </Grid>
                <Grid item xs={4} md={2} lg={2}>
                    <TextField
                        fullWidth
                        name="description"
                        inlineLabel="Description"
                        value={values.description}
                        placeholder="Search by description"
                        onChange={handleChange}
                        customStyle={classes.inputField}
                    />
                </Grid>
                <Grid item xs={4} md={2} lg={2}>
                    <Autocomplete
                        options={matches}
                        labelKey="label"
                        valueKey="value"
                        placeholder="Has Matches"
                        name="hasMatches"
                        inlineLabel="Has Matches"
                        value={values.hasMatches}
                        onChange={(e, value) => setFieldValue('hasMatches', value)}
                        isSearchField={false}
                        disableClearable
                        customStyle={classes.dropdown}
                    />
                </Grid>
                <Grid item xs={4} md={2} lg={2}>
                    <Autocomplete
                        options={types}
                        labelKey="label"
                        valueKey="value"
                        placeholder="Type"
                        name="type"
                        inlineLabel="Type"
                        value={values.type}
                        onChange={(e, value) => setFieldValue('type', value)}
                        isSearchField={false}
                        disableClearable
                        customStyle={classes.dropdown}
                    />
                </Grid>
            </Grid>

            <Grid container spacing={2} alignItems="center" justifyContent="space-between">
                {data && data?.count > 0 && (
                    <Grid item marginTop="10px" marginBottom="10px">
                        <Button
                            text="Auto Match"
                            startIcon={<CachedIcon />}
                            loading={autoMatchLoading}
                            onClick={handleStartAutoMatch}
                            customStyles={classes.cancelButton}
                        />

                        <Button
                            text="Agent Matching"
                            data-tour="agent-match-button"
                            startIcon={<AutoFixNormalIcon />}
                            loading={autoMatchLoading}
                            onClick={handleAIMatch}
                        />
                    </Grid>
                )}

                <Grid item marginTop="10px" marginBottom="10px" marginLeft="auto">
                    {selected?.length > 0 && (
                        <Button
                            text="Exclude All"
                            customStyles={classes.excludeAllButton}
                            onClick={hangleExcludeAllButtonClick}
                            loading={isLoading}
                        />
                    )}
                    <Button
                        text="Clear"
                        onClick={() => handleClearClick(handleReset)}
                        customStyles={classes.cancelButton}
                    />
                    <Button text="Apply" type="submit" />
                </Grid>
            </Grid>
        </Fragment>
    );
};

export default ForReviewFilters;
